import { useEffect, useMemo } from 'react';
import { Form } from 'antd';
import { HR_DOCUMENTS_OPTIONS } from '@/shared/company';

/**
 * Keys, permission states and watched values of the general data and application settings fields.
 */
export default function useCompanyGeneralFields({
  INPUT_KEYS,
  form,
  getFieldState,
}) {
  // * Global data form & values
  const ownershipStructureKeys = INPUT_KEYS.ownershipStructure;
  const ownershipStructureState = getFieldState([ownershipStructureKeys]);
  const ownershipStructureValues = Form.useWatch(ownershipStructureKeys, form);

  const companyNameKeys = 'name';
  const companyNameState = getFieldState([companyNameKeys]);

  // * General data form
  const codeKeys = INPUT_KEYS.generalData.code;
  const codeState = getFieldState(codeKeys);

  const pibKeys = INPUT_KEYS.generalData.pib;
  const pibState = getFieldState(pibKeys);

  const cityKeys = INPUT_KEYS.generalData.city;
  const cityState = getFieldState(cityKeys);

  const zipKeys = INPUT_KEYS.generalData.zip;
  const zipState = getFieldState(zipKeys);

  const addressKeys = INPUT_KEYS.generalData.address;
  const addressState = getFieldState(addressKeys);

  const emailKeys = INPUT_KEYS.generalData.email;
  const emailState = getFieldState(emailKeys);

  const phoneNumberKeys = INPUT_KEYS.generalData.phoneNumber;
  const phoneNumberState = getFieldState(phoneNumberKeys);

  const bankAccountKeys = INPUT_KEYS.generalData.bankAccount;
  const bankAccountState = getFieldState(bankAccountKeys);

  const apiKeyKeys = INPUT_KEYS.generalData.apiKey;
  const apiKeyState = getFieldState(apiKeyKeys);

  const mainCompanyKeys = INPUT_KEYS.generalData.mainCompany;
  const mainCompanyState = getFieldState(mainCompanyKeys);

  const emailReceiversKeys = INPUT_KEYS.generalData.emailReceivers;
  const emailReceiversState = getFieldState([emailReceiversKeys[1]]);

  const marketKeys = INPUT_KEYS.market;
  const marketState = getFieldState(marketKeys);
  const selectedMarket = Form.useWatch(marketKeys, form) || 'RS';

  // * Application Settings data form
  const typeOfCompanyKeys = INPUT_KEYS.applicationSettings.typeOfCompany;
  const typeOfCompanyState = getFieldState(typeOfCompanyKeys);

  const typeOfHrKeys = INPUT_KEYS.applicationSettings.typeOfHr;
  const typeOfHrState = getFieldState(typeOfHrKeys);

  // Filter HR document options by selected market
  const filteredHrDocumentsOptions = useMemo(
    () =>
      HR_DOCUMENTS_OPTIONS.filter((opt) =>
        opt.markets?.includes(selectedMarket)
      ),
    [selectedMarket]
  );

  // Auto-reset typeOfHr when market changes and current value is invalid
  const selectedTypeOfHr = Form.useWatch(typeOfHrKeys, form);
  useEffect(() => {
    if (!selectedTypeOfHr) return;
    const validForMarket = HR_DOCUMENTS_OPTIONS.some(
      (opt) =>
        opt.value === selectedTypeOfHr && opt.markets?.includes(selectedMarket)
    );
    if (!validForMarket) {
      const defaultForMarket = HR_DOCUMENTS_OPTIONS.find((opt) =>
        opt.markets?.includes(selectedMarket)
      );
      if (defaultForMarket) {
        form.setFieldValue(typeOfHrKeys, defaultForMarket.value);
      }
    }
  }, [selectedMarket, selectedTypeOfHr, form, typeOfHrKeys]);

  const isHrKeys = INPUT_KEYS.applicationSettings.isHr;
  const isHrState = getFieldState(isHrKeys);

  const isSectorKeys = INPUT_KEYS.applicationSettings.isSector;
  const isSectorState = getFieldState(isSectorKeys);

  const holidayDaysTrackerKeys =
    INPUT_KEYS.applicationSettings.holidayDaysTracker;
  const holidayDaysTrackerState = getFieldState(holidayDaysTrackerKeys);

  const strictOldHolidayCutoffKeys =
    INPUT_KEYS.applicationSettings.strictOldHolidayCutoff;
  const strictOldHolidayCutoffState = getFieldState(strictOldHolidayCutoffKeys);

  const holidayFirstPartRequiredKeys =
    INPUT_KEYS.applicationSettings.holidayFirstPartRequired;
  const holidayFirstPartRequiredState = getFieldState(
    holidayFirstPartRequiredKeys
  );

  const trackerStartDateKeys = INPUT_KEYS.applicationSettings.trackerStartDate;
  const trackerStartDateState = getFieldState(trackerStartDateKeys);

  const workFromHomeTypeKeys = INPUT_KEYS.applicationSettings.workFromHomeType;
  const workFromHomeTypeState = getFieldState(workFromHomeTypeKeys);

  const eInvoiceKeys = INPUT_KEYS.applicationSettings.eInvoice;
  const eInvoiceState = getFieldState(eInvoiceKeys);

  const documentReturnTypeKeys =
    INPUT_KEYS.applicationSettings.documentReturnType;
  const documentReturnTypeState = getFieldState(documentReturnTypeKeys);

  const documentSeparateRequestResponseKeys =
    INPUT_KEYS.applicationSettings.documentSeparateRequestResponse;
  const documentSeparateRequestResponseState = getFieldState(
    documentSeparateRequestResponseKeys
  );

  const foreignWorkPermitTrackerKeys =
    INPUT_KEYS.applicationSettings.foreignWorkPermitTracker;
  const foreignWorkPermitTrackerState = getFieldState(
    foreignWorkPermitTrackerKeys
  );

  const weekendLeavesKeys = INPUT_KEYS.applicationSettings.weekendLeaves;
  const weekendLeavesState = getFieldState(weekendLeavesKeys);

  const qrInEInvoicesKeys = INPUT_KEYS.applicationSettings.qrInEInvoices;
  const qrInEInvoicesState = getFieldState(qrInEInvoicesKeys);

  const isForEmployeesKeys = INPUT_KEYS.applicationSettings.isForEmployees;
  const isForEmployeesState = getFieldState(isForEmployeesKeys);

  const isHrOfficerSelfApproveKeys =
    INPUT_KEYS.applicationSettings.isHrOfficerSelfApprove;
  const isHrOfficerSelfApproveState = getFieldState(isHrOfficerSelfApproveKeys);

  const companyAdminsReceiveHREmailsKeys =
    INPUT_KEYS.applicationSettings.companyAdminsReceiveHREmails;
  const companyAdminsReceiveHREmailsState = getFieldState(
    companyAdminsReceiveHREmailsKeys
  );

  const isGrossBonusKeys = INPUT_KEYS.applicationSettings.isGrossBonus;
  const isGrossBonusState = getFieldState(isGrossBonusKeys);

  const showInternalRefNumberKeys =
    INPUT_KEYS.applicationSettings.showInternalRefNumber;
  const showInternalRefNumberState = getFieldState(showInternalRefNumberKeys);

  const invoiceProjectEnabledKeys =
    INPUT_KEYS.applicationSettings.invoiceProjectEnabled;
  const invoiceProjectEnabledState = getFieldState(invoiceProjectEnabledKeys);

  const manualPurchaseInvoiceEnabledKeys =
    INPUT_KEYS.applicationSettings.manualPurchaseInvoiceEnabled;
  const manualPurchaseInvoiceEnabledState = getFieldState(
    manualPurchaseInvoiceEnabledKeys
  );

  const invoiceJournalEnabledKeys =
    INPUT_KEYS.applicationSettings.invoiceJournalEnabled;
  const invoiceJournalEnabledState = getFieldState(invoiceJournalEnabledKeys);

  const salesJournalEnabledKeys =
    INPUT_KEYS.applicationSettings.salesJournalEnabled;
  const salesJournalEnabledState = getFieldState(salesJournalEnabledKeys);

  const vatNoticeEnabledKeys = INPUT_KEYS.applicationSettings.vatNoticeEnabled;
  const vatNoticeEnabledState = getFieldState(vatNoticeEnabledKeys);

  const advancedSalesInvoicingEnabledKeys =
    INPUT_KEYS.applicationSettings.advancedSalesInvoicingEnabled;
  const advancedSalesInvoicingEnabledState = getFieldState(
    advancedSalesInvoicingEnabledKeys
  );

  const autoArchiveInvoicesEnabledKeys =
    INPUT_KEYS.applicationSettings.autoArchiveInvoicesEnabled;
  const autoArchiveInvoicesEnabledState = getFieldState(
    autoArchiveInvoicesEnabledKeys
  );
  // Absent on every company saved before the switch existed, and absent means
  // on — the same reading as the server's `$ne: false`.
  const autoArchiveInvoicesOn =
    Form.useWatch(autoArchiveInvoicesEnabledKeys, form) !== false;

  const autoArchiveAfterMonthsKeys =
    INPUT_KEYS.applicationSettings.autoArchiveAfterMonths;
  const autoArchiveAfterMonthsState = getFieldState(autoArchiveAfterMonthsKeys);

  const manualArchiveInvoicesEnabledKeys =
    INPUT_KEYS.applicationSettings.manualArchiveInvoicesEnabled;
  const manualArchiveInvoicesEnabledState = getFieldState(
    manualArchiveInvoicesEnabledKeys
  );

  const postedToErpTrackingEnabledKeys =
    INPUT_KEYS.applicationSettings.postedToErpTrackingEnabled;
  const postedToErpTrackingEnabledState = getFieldState(
    postedToErpTrackingEnabledKeys
  );
  const taxReturnsClientViewKeys =
    INPUT_KEYS.applicationSettings.taxReturnsClientView;
  const taxReturnsClientViewState = getFieldState(taxReturnsClientViewKeys);
  const ledgerCardsEnabledKeys =
    INPUT_KEYS.applicationSettings.ledgerCardsEnabled;
  const ledgerCardsEnabledState = getFieldState(ledgerCardsEnabledKeys);
  const sefCustomsEnabledKeys =
    INPUT_KEYS.applicationSettings.sefCustomsEnabled;
  const sefCustomsEnabledState = getFieldState(sefCustomsEnabledKeys);
  const sefInputVatEnabledKeys =
    INPUT_KEYS.applicationSettings.sefInputVatEnabled;
  const sefInputVatEnabledState = getFieldState(sefInputVatEnabledKeys);
  const sefVatRecordingEnabledKeys =
    INPUT_KEYS.applicationSettings.sefVatRecordingEnabled;
  const sefVatRecordingEnabledState = getFieldState(sefVatRecordingEnabledKeys);
  const sefFiscalBillsEnabledKeys =
    INPUT_KEYS.applicationSettings.sefFiscalBillsEnabled;
  const sefFiscalBillsEnabledState = getFieldState(sefFiscalBillsEnabledKeys);

  const isSickLeaveRequestKeys =
    INPUT_KEYS.applicationSettings.isSickLeaveRequest;
  const isSickLeaveRequestState = getFieldState(isSickLeaveRequestKeys);

  const timeTrackingKeys = INPUT_KEYS.applicationSettings.timeTracking;
  const timeTrackingState = getFieldState(timeTrackingKeys);

  // * Time Tracking Settings
  const requireApprovalKeys = INPUT_KEYS.timeTrackingSettings.requireApproval;
  const autoSyncToPayrollKeys =
    INPUT_KEYS.timeTrackingSettings.autoSyncToPayroll;
  const defaultBillableKeys = INPUT_KEYS.timeTrackingSettings.defaultBillable;
  const allowTimerKeys = INPUT_KEYS.timeTrackingSettings.allowTimer;
  const maxHoursPerDayKeys = INPUT_KEYS.timeTrackingSettings.maxHoursPerDay;
  const attendanceModeKeys = INPUT_KEYS.timeTrackingSettings.attendanceMode;
  const lockAfterApprovalKeys =
    INPUT_KEYS.timeTrackingSettings.lockAfterApproval;

  // * Timesheet Review Settings
  const timesheetReviewEnabledKeys =
    INPUT_KEYS.applicationSettings.timesheetReviewEnabled;
  const timesheetReviewEnabledState = getFieldState(timesheetReviewEnabledKeys);
  const timesheetReviewMaxHoursKeys =
    INPUT_KEYS.timesheetReviewRules.maxHoursPerDay;
  const timesheetReviewFlagOTKeys =
    INPUT_KEYS.timesheetReviewRules.flagOTSameDayAsPublicHoliday;
  const timesheetReviewFlagNightKeys =
    INPUT_KEYS.timesheetReviewRules.flagNightWithRegularEqFond;
  const timesheetReviewFlagPartialKeys =
    INPUT_KEYS.timesheetReviewRules.flagPartialPublicHoliday;
  const timesheetReviewFlagUnmarkedOtKeys =
    INPUT_KEYS.timesheetReviewRules.flagUnmarkedOvertime;

  // * Module Visibility (per-company gates for Tools and Calculator)
  const toolsEnabledKeys = INPUT_KEYS.applicationSettings.toolsEnabled;
  const toolsEnabledState = getFieldState(toolsEnabledKeys);
  const calculatorEnabledKeys =
    INPUT_KEYS.applicationSettings.calculatorEnabled;
  const calculatorEnabledState = getFieldState(calculatorEnabledKeys);
  const assistantEnabledKeys = INPUT_KEYS.applicationSettings.assistantEnabled;
  const assistantEnabledState = getFieldState(assistantEnabledKeys);
  const activityFeedEnabledKeys =
    INPUT_KEYS.applicationSettings.activityFeedEnabled;
  const activityFeedEnabledState = getFieldState(activityFeedEnabledKeys);

  const mfaEnabledKeys = INPUT_KEYS.applicationSettings.mfaEnabled;
  const mfaEnabledState = getFieldState(mfaEnabledKeys);

  const ssoOnlyKeys = INPUT_KEYS.applicationSettings.ssoOnly;
  const ssoOnlyState = getFieldState(ssoOnlyKeys);
  const ssoTenantIdKeys = INPUT_KEYS.applicationSettings.ssoTenantId;
  const ssoTenantIdState = getFieldState(ssoTenantIdKeys);
  const ssoAllowedDomainsKeys =
    INPUT_KEYS.applicationSettings.ssoAllowedDomains;
  const ssoAllowedDomainsState = getFieldState(ssoAllowedDomainsKeys);

  const typeOfCompany = Form.useWatch(typeOfCompanyKeys, form);
  const eInvoiceChecked = Form.useWatch(eInvoiceKeys, form);
  const isHrChecked = Form.useWatch(isHrKeys, form);
  const holidayDaysTrackerChecked = Form.useWatch(holidayDaysTrackerKeys, form);
  const isSectorChecked = Form.useWatch(isSectorKeys, form);
  const isWorkFromHomeChecked = Form.useWatch(workFromHomeTypeKeys, form);
  const loyaltyEnabled = Form.useWatch(
    ['loyaltyHolidaySettings', 'enabled'],
    form
  );
  const timeTrackingChecked = Form.useWatch(timeTrackingKeys, form);
  const timesheetReviewEnabledChecked = Form.useWatch(
    timesheetReviewEnabledKeys,
    form
  );

  return {
    activityFeedEnabledKeys,
    activityFeedEnabledState,
    addressKeys,
    addressState,
    advancedSalesInvoicingEnabledKeys,
    advancedSalesInvoicingEnabledState,
    allowTimerKeys,
    apiKeyKeys,
    apiKeyState,
    assistantEnabledKeys,
    assistantEnabledState,
    attendanceModeKeys,
    autoArchiveAfterMonthsKeys,
    autoArchiveAfterMonthsState,
    autoArchiveInvoicesEnabledKeys,
    autoArchiveInvoicesEnabledState,
    autoArchiveInvoicesOn,
    autoSyncToPayrollKeys,
    bankAccountKeys,
    bankAccountState,
    calculatorEnabledKeys,
    calculatorEnabledState,
    cityKeys,
    cityState,
    codeKeys,
    codeState,
    companyAdminsReceiveHREmailsKeys,
    companyAdminsReceiveHREmailsState,
    companyNameKeys,
    companyNameState,
    defaultBillableKeys,
    documentReturnTypeKeys,
    documentReturnTypeState,
    documentSeparateRequestResponseKeys,
    documentSeparateRequestResponseState,
    eInvoiceChecked,
    eInvoiceKeys,
    eInvoiceState,
    emailKeys,
    emailReceiversKeys,
    emailReceiversState,
    emailState,
    filteredHrDocumentsOptions,
    foreignWorkPermitTrackerKeys,
    foreignWorkPermitTrackerState,
    holidayDaysTrackerChecked,
    holidayDaysTrackerKeys,
    holidayDaysTrackerState,
    invoiceJournalEnabledKeys,
    invoiceJournalEnabledState,
    invoiceProjectEnabledKeys,
    invoiceProjectEnabledState,
    isForEmployeesKeys,
    isForEmployeesState,
    isGrossBonusKeys,
    isGrossBonusState,
    isHrChecked,
    isHrKeys,
    isHrOfficerSelfApproveKeys,
    isHrOfficerSelfApproveState,
    isHrState,
    isSectorChecked,
    isSectorKeys,
    isSectorState,
    isSickLeaveRequestKeys,
    isSickLeaveRequestState,
    isWorkFromHomeChecked,
    lockAfterApprovalKeys,
    loyaltyEnabled,
    mainCompanyKeys,
    mainCompanyState,
    manualArchiveInvoicesEnabledKeys,
    manualArchiveInvoicesEnabledState,
    manualPurchaseInvoiceEnabledKeys,
    manualPurchaseInvoiceEnabledState,
    marketKeys,
    marketState,
    maxHoursPerDayKeys,
    mfaEnabledKeys,
    mfaEnabledState,
    ownershipStructureKeys,
    ownershipStructureState,
    ownershipStructureValues,
    phoneNumberKeys,
    phoneNumberState,
    pibKeys,
    pibState,
    postedToErpTrackingEnabledKeys,
    postedToErpTrackingEnabledState,
    qrInEInvoicesKeys,
    qrInEInvoicesState,
    requireApprovalKeys,
    salesJournalEnabledKeys,
    salesJournalEnabledState,
    showInternalRefNumberKeys,
    showInternalRefNumberState,
    ssoAllowedDomainsKeys,
    ssoAllowedDomainsState,
    ssoOnlyKeys,
    ssoOnlyState,
    ssoTenantIdKeys,
    ssoTenantIdState,
    strictOldHolidayCutoffKeys,
    strictOldHolidayCutoffState,
    holidayFirstPartRequiredKeys,
    holidayFirstPartRequiredState,
    taxReturnsClientViewKeys,
    taxReturnsClientViewState,
    ledgerCardsEnabledKeys,
    ledgerCardsEnabledState,
    sefCustomsEnabledKeys,
    sefCustomsEnabledState,
    sefInputVatEnabledKeys,
    sefInputVatEnabledState,
    sefVatRecordingEnabledKeys,
    sefVatRecordingEnabledState,
    sefFiscalBillsEnabledKeys,
    sefFiscalBillsEnabledState,
    timeTrackingChecked,
    timeTrackingKeys,
    timeTrackingState,
    timesheetReviewEnabledChecked,
    timesheetReviewEnabledKeys,
    timesheetReviewEnabledState,
    timesheetReviewFlagNightKeys,
    timesheetReviewFlagOTKeys,
    timesheetReviewFlagPartialKeys,
    timesheetReviewFlagUnmarkedOtKeys,
    timesheetReviewMaxHoursKeys,
    toolsEnabledKeys,
    toolsEnabledState,
    trackerStartDateKeys,
    trackerStartDateState,
    typeOfCompany,
    typeOfCompanyKeys,
    typeOfCompanyState,
    typeOfHrKeys,
    typeOfHrState,
    vatNoticeEnabledKeys,
    vatNoticeEnabledState,
    weekendLeavesKeys,
    weekendLeavesState,
    workFromHomeTypeKeys,
    workFromHomeTypeState,
    zipKeys,
    zipState,
  };
}
